import { useState, useRef, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { knobVariants as variants } from '../data/productLine'
import { useTranslation } from 'react-i18next'

gsap.registerPlugin(ScrollTrigger)

const cardVariants = {
  enter: (direction) => ({
    y: direction > 0 ? 40 : -40,
    opacity: 0,
    scale: 0.95,
    filter: 'blur(8px)',
  }),
  center: {
    y: 0,
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: { duration: 0.6, ease: [0.33, 1, 0.68, 1] },
  },
  exit: (direction) => ({
    y: direction > 0 ? -40 : 40,
    opacity: 0,
    scale: 0.95,
    filter: 'blur(8px)',
    transition: { duration: 0.35, ease: [0.33, 1, 0.68, 1] },
  }),
}

export default function VolumeKnob() {
  const { t } = useTranslation()
  const [active, setActive] = useState(3) // Ghost default
  const [direction, setDirection] = useState(0)
  const sectionRef = useRef(null)

  const trackRef = useRef(null)
  const isDragging = useRef(false)
  const activeRef = useRef(active)
  activeRef.current = active

  const barRandomHeights = useMemo(() =>
    Array.from({ length: 32 }, () => 0.3 + Math.random() * 0.7), [])

  function selectVariant(idx) {
    setDirection(idx > activeRef.current ? 1 : -1)
    setActive(idx)
  }

  function findNearestVariant(pct) {
    let nearest = 0
    let minDist = Infinity
    variants.forEach((v, i) => {
      const dist = Math.abs(v.fill - pct)
      if (dist < minDist) { minDist = dist; nearest = i }
    })
    return nearest
  }

  function getClientX(e) {
    if (e.touches && e.touches.length > 0) return e.touches[0].clientX
    if (e.changedTouches && e.changedTouches.length > 0) return e.changedTouches[0].clientX
    return e.clientX
  }

  function handleTrackClick(e) {
    if (!trackRef.current) return
    const rect = trackRef.current.getBoundingClientRect()
    const pct = Math.max(0, Math.min(100, ((getClientX(e) - rect.left) / rect.width) * 100))
    const idx = findNearestVariant(pct)
    selectVariant(idx)
  }

  useEffect(() => {
    function onMove(e) {
      if (!isDragging.current || !trackRef.current) return
      const rect = trackRef.current.getBoundingClientRect()
      const pct = Math.max(0, Math.min(100, ((getClientX(e) - rect.left) / rect.width) * 100))
      const idx = findNearestVariant(pct)
      if (idx !== activeRef.current) selectVariant(idx)
    }
    function onEnd() {
      isDragging.current = false
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
  }, [])

  function handleThumbDown(e) {
    e.stopPropagation()
    isDragging.current = true
    document.body.style.userSelect = 'none'
  }

  const current = variants[active]

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.volume-section .section-label', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.volume-section',
          start: 'top 70%',
        },
      })

      gsap.from('.volume-section h2', {
        y: 60,
        opacity: 0,
        duration: 1,
        delay: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.volume-section',
          start: 'top 70%',
        },
      })

      gsap.from('.volume-section .vol-sub', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        delay: 0.2,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.volume-section',
          start: 'top 70%',
        },
      })

      gsap.from('.knob-container', {
        y: 80,
        opacity: 0,
        duration: 1,
        delay: 0.3,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.knob-container',
          start: 'top 85%',
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      className="volume-section"
      id="system"
      ref={sectionRef}
      style={{ '--active-color': current.color }}
    >
      {/* Ambient glow that shifts with the selected variant */}
      <motion.div
        className="volume-ambient-glow"
        animate={{
          background: `radial-gradient(ellipse at 50% 50%, ${current.color} 0%, transparent 70%)`,
        }}
        transition={{ duration: 1.2, ease: 'easeInOut' }}
        aria-hidden="true"
      />

      <p className="section-label">{t('volume.label')}</p>
      <h2>{t('volume.title')}</h2>
      <p className="vol-sub">{t('volume.subtitle')}</p>

      <div className="knob-container">
        <div className="knob-track-wrap" onClick={handleTrackClick} onTouchStart={handleTrackClick} ref={trackRef}>
          <div className="knob-track">
            <motion.div
              className="knob-fill"
              animate={{
                width: `${current.fill}%`,
                background: `linear-gradient(90deg, var(--aftermath), ${current.color})`,
              }}
              transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
            />
            <motion.div
              className="knob-thumb"
              animate={{
                left: `${current.fill}%`,
                boxShadow: `0 0 24px ${current.color}`,
              }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
              whileHover={{ scale: 1.3 }}
              whileTap={{ scale: 0.9 }}
              onMouseDown={handleThumbDown}
              onTouchStart={() => { isDragging.current = true }}
              style={{ x: '-50%', y: '-50%' }}
              role="slider"
              tabIndex={0}
              aria-label="Fragrance intensity"
              aria-valuemin={0}
              aria-valuemax={variants.length - 1}
              aria-valuenow={active}
              aria-valuetext={`${current.name} — ${current.range}`}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  if (active < variants.length - 1) selectVariant(active + 1)
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                  e.preventDefault()
                  if (active > 0) selectVariant(active - 1)
                } else if (e.key === 'Home') {
                  e.preventDefault()
                  selectVariant(0)
                } else if (e.key === 'End') {
                  e.preventDefault()
                  selectVariant(variants.length - 1)
                }
              }}
            >
              <div className="inner" />
            </motion.div>
          </div>
        </div>

        {/* Frequency visualization bars */}
        <div className="freq-bars" aria-hidden="true">
          {Array.from({ length: 32 }, (_, i) => {
            const isActive = (i / 32) * 100 <= current.fill
            return (
              <motion.div
                key={i}
                className="freq-bar"
                animate={{
                  scaleY: isActive ? barRandomHeights[i] : 0.1,
                  backgroundColor: isActive ? current.color : 'var(--border)',
                }}
                transition={{
                  duration: 0.4,
                  delay: i * 0.015,
                  ease: [0.33, 1, 0.68, 1],
                }}
              />
            )
          })}
        </div>

        <div className="knob-nodes" role="tablist" aria-label="Fragrance variants">
          {variants.map((v, i) => (
            <motion.button
              key={v.name}
              className={`knob-node${i === active ? ' active' : ''}`}
              style={{ color: v.color }}
              onClick={() => selectVariant(i)}
              role="tab"
              aria-selected={i === active}
              aria-label={`${v.name} — ${v.range}`}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                className="knob-dot"
                animate={i === active ? {
                  scale: [1, 1.4, 1],
                } : {}}
                transition={{ duration: 0.5 }}
              />
              <span className="k-name">{v.name}</span>
              <span className="k-range">{v.range}</span>
            </motion.button>
          ))}
        </div>

        <div className="vol-detail" role="tabpanel" aria-live="polite">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={active}
              className="vol-card"
              custom={direction}
              variants={cardVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              <p className="vc-tagline" style={{ color: current.color }}>
                {current.tagline}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
